import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { colors } from '../theme';

interface Product {
  title: string;
  description: string;
  image: string;
  link: string;
}

const Products: React.FC = () => {
  const products: Product[] = [
    {
      title: 'Variable Frequency Drive (VFD) Panels',
      description: 'Control panels designed to withstand voltage surges, changing climate conditions, and job-specific space limitations.',
      image: '/product-image-1-1.jpg',
      link: '/products/vfd-panels',
    },
    {
      title: 'High-Density Linear & Crosslink Polyethylene Tanks',
      description: 'The largest product selection of chemical storage tanks in a variety of materials and sizes.',
      image: '/tanks.jpg',
      link: '/products/chemical-storage-tanks',
    },
    {
      title: 'Combination Starter Panels',
      description: 'Motor starter and electrical protection devices together for efficient operation.',
      image: '/starter.jpg',
      link: '/products/combination-starter-panels',
    },
    {
      title: 'Electrical Control Product',
      description: 'FIEPOS decentralized field power supplies with protection class IP54, IP65 or IP67.',
      image: '/electrical.jpg',
      link: '/products/electrical-control-product',
    },
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Page Header */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            width: '40%',
            height: '100%',
            background: 'linear-gradient(135deg, transparent 0%, rgba(255, 255, 255, 0.1) 100%)',
          },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography
              variant="overline"
              sx={{ color: 'rgba(255, 255, 255, 0.8)', fontWeight: 600, letterSpacing: 2, mb: 1, display: 'block' }}
            >
              What We Offer
            </Typography>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                color: '#FFFFFF',
                fontWeight: 700,
                mb: 2,
              }}
            >
              Products
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '1.1rem',
                maxWidth: 500,
              }}
            >
              Quality industrial equipment and custom solutions
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Accent Line */}
      <Box sx={{ height: 4, background: `linear-gradient(90deg, ${colors.secondary} 0%, ${colors.secondaryDark} 100%)` }} />

      {/* Main Content Section */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: '#FFFFFF',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '1.75rem', md: '2.25rem' },
                color: colors.secondary,
              }}
            >
              Select a Product Below for More Information
            </Typography>
          </Box>

          {/* Products Grid */}
          <Grid container spacing={4}>
            {products.map((product, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  component={Link}
                  to={product.link}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    textDecoration: 'none',
                    borderRadius: 3,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: colors.border,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 20px 50px rgba(245, 166, 35, 0.2)`,
                      borderColor: colors.primary,
                      '& img': {
                        transform: 'scale(1.05)',
                      },
                      '& .product-title': {
                        color: colors.primary,
                      },
                    },
                  }}
                >
                  <Box sx={{ overflow: 'hidden', height: 200 }}>
                    <Box
                      component="img"
                      src={product.image}
                      alt={product.title}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.4s ease',
                      }}
                    />
                  </Box>
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Typography
                      className="product-title"
                      variant="h6"
                      sx={{
                        color: colors.secondary,
                        fontWeight: 700,
                        mb: 1.5,
                        fontSize: '1.1rem',
                        transition: 'color 0.3s ease',
                      }}
                    >
                      {product.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: colors.textSecondary,
                        lineHeight: 1.6,
                      }}
                    >
                      {product.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Contact Us Button */}
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Button
              component={Link}
              to="/contact"
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
            >
              Contact Us for Custom Solutions
            </Button>
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: colors.secondary,
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: '1.75rem', md: '2.25rem' },
                color: '#FFFFFF',
                mb: 3,
              }}
            >
              Need a Custom Solution?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: '1.1rem',
                mb: 4,
                maxWidth: 500,
                mx: 'auto',
              }}
            >
              Our team of experts can help you design and build custom solutions tailored to your specific needs.
            </Typography>
            <Button
              component={Link}
              to="/contact"
              variant="contained"
              size="large"
              sx={{
                backgroundColor: colors.primary,
                '&:hover': {
                  backgroundColor: colors.primaryDark,
                },
              }}
            >
              Request a Quote
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Products;
